/**
 * CloudCredentialView — an opened cloud-credential (🔑) portfolio: one synced
 * from a cloud account rather than a file or the Add Instance form. Read-only.
 *
 * Port of legacy views/CloudUsagesReport/ListOfInstances.vue, shown on Manage
 * Portfolio in place of its cards (legacy gave it its own route, under the
 * same dashboard rail). Loads its instances (GET /get-portfolio, provider =
 * the cloud, lower-case, as legacy), and deletes the account's portfolio.
 *
 * Fixed from legacy:
 *  - Delete Account and the Advice button had NO permission check; they use
 *    the same switches as everywhere else (delete-portfolio, cost-advice);
 *  - a failed delete left the page stuck on its loader forever; here it says
 *    why and the portfolio stays open.
 *
 * No Operational Safety Margin slider, as legacy: its template has one, but it
 * never shows (it tests an `isEIA` that is not defined), and its value was
 * never sent anywhere — so showing it would only add a control that does
 * nothing. (The telemetry view does show it, as legacy.)
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ConfirmDialog } from '@components/common/ConfirmDialog';
import { PortfolioInstancesView } from '@components/common/PortfolioInstancesView';
import { Card, CardBody } from '@components/ui';
import { ADVICE_VIEWS, adviceLink, setAdviceView } from '@features/costadvice';
import { useNotify } from '@hooks/useNotify';
import { useAppMode } from '@variant/useAppMode';

import {
  readPortfolioDetails,
  useLazyGetPortfolioDetailsQuery,
} from '../../api/portfolioDetailsApi';
import { readDeleteResult, useDeletePortfolioMutation } from '../../api/uploadApi';
import { cloudCredentialColumns } from '../../cloudCredentialView';

import styles from './CloudCredentialView.module.css';

export function CloudCredentialView({
  portfolio,
  cloud,
  canDelete = false,
  canAdvice = false,
  canSeeRegions = false,
  onClose,
  onDeleted,
}) {
  const { mode } = useAppMode();
  const notify = useNotify();
  const columns = useMemo(() => cloudCredentialColumns(mode), [mode]);

  const [fetchDetails] = useLazyGetPortfolioDetailsQuery();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const loadSeq = useRef(0);

  useEffect(() => {
    loadSeq.current += 1;
    const seq = loadSeq.current;
    setDetails(null);
    if (!portfolio?.id) return;
    setLoading(true);
    (async () => {
      try {
        const loaded = readPortfolioDetails(
          await fetchDetails({
            id: portfolio.id,
            name: portfolio.name,
            user: portfolio.user,
            provider: String(cloud ?? '').toLowerCase(),
          }).unwrap(),
        );
        if (seq !== loadSeq.current) return;
        setDetails(loaded);
      } catch (error) {
        if (seq !== loadSeq.current) return;
        // Legacy's wording (ListOfInstances.vue :230).
        notify.error(error, 'An error occurred while fetching portfolio data. Please try again.');
        onClose?.();
      } finally {
        if (seq === loadSeq.current) setLoading(false);
      }
    })();
    // A different portfolio is what reloads it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolio?.id]);

  const [deletePortfolio] = useDeletePortfolioMutation();
  const [deleteDialog, setDeleteDialog] = useState(null); // null | 'open' | 'busy'
  const onConfirmDelete = async () => {
    setDeleteDialog('busy');
    try {
      const message = readDeleteResult(await deletePortfolio(portfolio.id).unwrap());
      setDeleteDialog(null);
      notify.success(message);
      onDeleted?.();
    } catch (error) {
      setDeleteDialog(null);
      notify.error(error, 'Error while deleting portfolio. Please try again.');
    }
  };

  /*
   * Instance Advice: the advice page on Analytics (legacy costAdviceRedirect,
   * ListOfInstances.vue :251-256). Nothing is handed over — this view has no
   * discount or margin of its own, so the portfolio's saved values apply.
   */
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const onAdvice = () => {
    if (!portfolio?.id) return;
    dispatch(setAdviceView(ADVICE_VIEWS.ANALYTICS));
    navigate(...adviceLink(portfolio.id));
  };

  return (
    <Card className={styles.card}>
      <CardBody className={styles.body}>
        <PortfolioInstancesView
          portfolio={portfolio}
          instances={details?.instances ?? []}
          columns={columns}
          loading={loading}
          cloud={cloud}
          canDelete={canDelete}
          canAdvice={canAdvice}
          canSeeRegions={canSeeRegions}
          busy={deleteDialog === 'busy'}
          deleteLabel="Delete Account"
          onDelete={() => setDeleteDialog('open')}
          onClose={onClose}
          onAdvice={onAdvice}
          showCancel
        />
      </CardBody>

      {deleteDialog ? (
        // Legacy's wording and buttons (ListOfInstances.vue :82-96).
        <ConfirmDialog
          title="Confirm Delete Portfolio"
          message="Are you sure you want to delete this Portfolio?"
          cancelLabel="Cancel"
          confirmLabel="Delete"
          danger
          busy={deleteDialog === 'busy'}
          onConfirm={onConfirmDelete}
          onCancel={() => setDeleteDialog(null)}
        />
      ) : null}
    </Card>
  );
}

export default CloudCredentialView;
